import { isNotFound, Link } from '@tanstack/react-router';
import { usePortalContext } from '@/lib/use-portal-context';
import { useTranslation } from '@/lib/i18n';
import { Card, CardContent, CardFooter } from '@worldcoo/ui/components/card';
import { CampaignStatistics } from './statistics-campaign';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { Button, buttonVariants } from '@worldcoo/ui/components/button';
import { cn, formatNumber } from '@worldcoo/ui/lib/utils';
import { Suspense } from 'react';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { RingProgressBar } from './ring-progress-bar';
import { Separator } from '@worldcoo/ui/components/separator';
import { useClient } from '@worldcoo/ui/hooks/use-client';
import { useDebounce } from '@worldcoo/ui/hooks/use-debounce';
import { Image } from '../../-components/image';
import {
	usePortalAuthorQuery,
	usePortalCampaignQuery,
	usePortalCampaignsAggregatesQuery,
	usePortalMetadataQuery,
} from '@/lib/queries';
import { PortalAnimateNumber } from './portal-animate-number';
import { SdgIcon } from './sdg-icons';

export function CampaignDetails(props: { campaignId: string }) {
	return (
		<Suspense>
			<SuspensedCampaignDetails campaignId={props.campaignId} />
		</Suspense>
	);
}

function SuspensedCampaignDetails(props: { campaignId: string }) {
	return (
		<aside className="flex flex-col gap-6">
			<DivWithPrimaryColorVariable>
				<Card className="shadow-4xl border-none">
					<CardContent className="flex-gap flex flex-col gap-4">
						<Suspense fallback={<Skeleton />}>
							<CampaignProgress campaignId={props.campaignId} />
						</Suspense>

						<CampaignStatistics campaignId={props.campaignId} variant="sm" textEffectDisabled />
					</CardContent>
					<CardFooter className="hidden lg:flex">
						<DonateButton campaignId={props.campaignId} />
					</CardFooter>
				</Card>
			</DivWithPrimaryColorVariable>

			<CampaignNgos campaignId={props.campaignId} />
		</aside>
	);
}

function CampaignProgress(props: { campaignId: string }) {
	const { i18n, language } = useTranslation();
	const metadata = usePortalMetadataQuery();
	const campaign = usePortalCampaignQuery(props.campaignId);
	const aggregates = usePortalCampaignsAggregatesQuery();
	const isClient = useClient();
	const campaignAggregate =
		aggregates && 'aggregates' in aggregates ? aggregates.aggregates[props.campaignId] : undefined;
	const funding = campaignAggregate?.funding;
	const progressValue = campaignAggregate?.progress ?? 0;
	const progress = useDebounce(
		((!('isPageLoaded' in campaign) || !campaign.isPageLoaded) && !isClient) || funding == null
			? 0
			: Math.ceil(progressValue),
		150,
	);

	if (funding == null || isNotFound(campaign) || isNotFound(metadata)) {
		return null;
	}

	if (metadata.hideStatsOnActiveCampaigns && !campaignAggregate?.finished) {
		return null;
	}

	return (
		<>
			<div className="flex items-end gap-5">
				<RingProgressBar
					radius={40}
					stroke={6}
					progress={Math.min(progress, 100)}
					strokeColor="var(--primary)"
					color="var(--primary)"
				/>
				<div className="flex flex-1 flex-col">
					<span className="inline-flex text-[32px] leading-[100%]">
						<PortalAnimateNumber locale={language} value={progress} suffix="%" />
					</span>
					<span className="text-primary text-xs font-semibold lg:text-base">{i18n.actions.funded}</span>
				</div>
			</div>

			<Separator />
		</>
	);
}

function DonateButton(props: { campaignId: string }) {
	const { i18n } = useTranslation();
	const { portalId } = usePortalContext();
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (!portalId || isNotFound(campaign)) return null;

	return campaign.data.finished ? (
		<Button size="xl" className="mt-auto w-full" disabled>
			{i18n.portal.campaignFunded}
		</Button>
	) : (
		<Link
			to="/$portalId/$campaignId/donate"
			params={{ portalId, campaignId: campaign.data.id }}
			search={(prev) => prev}
			className={cn(buttonVariants({ size: 'xl' }), 'mt-auto w-full')}
		>
			{i18n.actions.makeDonation}
		</Link>
	);
}

function useIsSingleNgoPortal(ngos: Array<{ id: string }>) {
	const author = usePortalAuthorQuery();
	if (isNotFound(author)) return false;
	return author.type === 'ngo' && ngos.length === 1 && ngos[0]?.id === author.ngoId;
}

function CampaignNgos(props: { campaignId: string }) {
	const { i18n } = useTranslation();
	const campaign = usePortalCampaignQuery(props.campaignId);
	const isSingleNgo = useIsSingleNgoPortal(isNotFound(campaign) ? [] : campaign.data.ngos);

	if (isNotFound(campaign)) return null;

	return (
		<>
			<DivWithPrimaryColorVariable className="flex flex-col gap-3">
				<Card className="shadow-4xl border-none">
					<CardContent className="flex-gap flex flex-col gap-4">
						<Suspense fallback={<Skeleton />}>
							<Beneficiaries campaignId={props.campaignId} />
						</Suspense>

						{!isSingleNgo && (
							<>
								{i18n.portal.aCampaignFrom.replace('{{ngoName}}', '')}
								<ul className="flex flex-wrap justify-evenly">
									{campaign.data.ngos
										// There is not a wide support for toSorted() in the browser
										// oxlint-disable-next-line unicorn/no-array-sort
										.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0)
										.filter((ngo) => ngo.logo !== undefined)
										.map((ngo) => (
											<li key={ngo.id} className="m-auto p-2">
												<Image
													className="m-auto object-contain! p-2"
													src={ngo.logo!.location}
													objectFit="contain"
													height={96}
													aspectRatio={ngo.logo!.width / ngo.logo!.height}
													alt={ngo.name ?? ''}
												/>
											</li>
										))}
								</ul>
							</>
						)}
					</CardContent>
				</Card>
			</DivWithPrimaryColorVariable>

			<SdgGoals campaignId={props.campaignId} />

			{campaign.data.ngos.some((ngo) => ngo.accreditedByLealtad) && (
				<Image
					src="/accredited-by-lealtad.png"
					height={57}
					aspectRatio={128 / 57}
					// TODO: translate for accessibility
					alt="accredited by lealtad"
				/>
			)}
		</>
	);
}

function Beneficiaries(props: { campaignId: string }) {
	const { i18n, language } = useTranslation();
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (isNotFound(campaign)) return;
	if (!campaign.data.beneficiaries.direct || !campaign.data.beneficiaries.indirect) return;

	return (
		<>
			{i18n.portal.beneficiaries}

			<div className="flex items-center gap-6">
				<div className="flex items-center gap-2">
					<span className="font-[Helvetica] text-xl tracking-tight">
						{formatNumber(language, campaign.data.beneficiaries.direct, 0)}
					</span>
					<span className="text-primary text-sm font-medium">
						{i18n.portal.directBeneficiaries.replace('{{count}}', '')}
					</span>
				</div>

				<div className="flex items-center gap-2">
					<span className="font-[Helvetica] text-xl tracking-tight">
						{formatNumber(language, campaign.data.beneficiaries.indirect, 0)}
					</span>
					<span className="text-primary text-sm font-medium">
						{i18n.portal.indirectBeneficiaries.replace('{{count}}', '')}
					</span>
				</div>
			</div>

			<Separator />
		</>
	);
}

function SdgGoals(props: { campaignId: string }) {
	const { i18n } = useTranslation();
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (isNotFound(campaign)) return null;
	if (!campaign.data.sdgs || campaign.data.sdgs.length === 0) return null;

	const sdgTranslations = i18n.portal.sdgs;

	return (
		<DivWithPrimaryColorVariable className="flex flex-col gap-3">
			<Card className="shadow-4xl border-none">
				<CardContent className="flex flex-col gap-3">
					<span className="text-sm font-medium">{sdgTranslations.title}</span>
					<ul className="flex flex-col gap-2">
						{campaign.data.sdgs.map((sdgNumber) => (
							<li key={sdgNumber} className="flex items-center gap-2">
								<SdgIcon goal={sdgNumber} className="size-10 shrink-0 rounded" />
								<span className="text-sm">{sdgTranslations[sdgNumber as keyof typeof sdgTranslations]}</span>
							</li>
						))}
					</ul>
				</CardContent>
			</Card>
		</DivWithPrimaryColorVariable>
	);
}
